/**
 * Status Page pública — `/status`
 *
 * Mostra estado geral + cada check de /health/full em tempo real.
 * Atualiza a cada 30s. Pública (sem auth).
 *
 * Uso da plataforma: clientes acessam quando suspeitam de instabilidade.
 * Bom pra reduzir "Why is it slow?" emails.
 *
 * Limitação: mostra apenas o estado **atual**. Histórico (uptime %, incidents)
 * pode ser adicionado depois com persistência (Supabase logs ou serviço externo
 * tipo Better Stack). Pra MVP, snapshot atual basta.
 */
import Head from "next/head";
import Link from "next/link";
import { useEffect, useState } from "react";
import { Brand } from "@/components/Brand";
import { SmartBackLink } from "@/components/SmartBackLink";
import { useTranslation } from "react-i18next";  // R25_I18N_DONE

interface CheckResult {
  ok:         boolean;
  latency_ms: number;
  error?:     string;
  skipped?:   boolean;
}

interface HealthFull {
  ok:      boolean;
  env:     string;
  version: string;
  service: string;
  checks: {
    database:      CheckResult;
    supabase_jwks: CheckResult;
    openai:        CheckResult;
  };
}

const POLL_INTERVAL_MS = 30_000;
const API_URL = process.env.NEXT_PUBLIC_API_URL || "";


export default function StatusPage() {
  const { t } = useTranslation();
  const [data, setData]       = useState<HealthFull | null>(null);
  const [error, setError]     = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<Date | null>(null);

  async function fetchStatus() {
    try {
      // Em produção, /api é proxied pelo Caddy pro backend
      const url = API_URL ? `${API_URL}/health/full` : "/api/health/full";
      const r = await fetch(url, { cache: "no-store" });
      const j = await r.json();
      setData(j);
      setError(null);
    } catch (e: any) {
      setError(e.message || "Erro ao buscar status");
    } finally {
      setLastFetch(new Date());
    }
  }

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  // Estado geral: tudo verde, ou parcial, ou tudo vermelho
  const allOk    = data?.ok === true;
  const hasError = !!error || (data && !data.ok);
  const services = data ? [
    { name: "API",      key: "api",            ok: true, lat: null as number | null }, // a página tá rodando = API tá OK
    { name: "Database", key: "database",       ok: data.checks.database.ok,      lat: data.checks.database.latency_ms,      error: data.checks.database.error },
    { name: "Auth",     key: "supabase_jwks",  ok: data.checks.supabase_jwks.ok, lat: data.checks.supabase_jwks.latency_ms, error: data.checks.supabase_jwks.error },
    { name: "AI",       key: "openai",         ok: data.checks.openai.ok,        lat: data.checks.openai.latency_ms,        error: data.checks.openai.error, skipped: data.checks.openai.skipped },
  ] : [];

  return (
    <>
      <Head>
        <title>Status — Miller Platform</title>
        <meta name="description" content="Status em tempo real dos serviços da Miller Platform." />
      </Head>

      <div className="min-h-screen bg-app-bg">
        <header className="border-b border-app bg-app-surface">
          <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
            <Link href="/" className="hover:opacity-80 transition-opacity">
              <Brand />
            </Link>
            <SmartBackLink className="text-sm text-app-2 hover:text-app-1 font-medium">
              Voltar
            </SmartBackLink>
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-6 py-12">
          <p className="text-xs font-semibold text-app-3 uppercase tracking-widest mb-2">
            STATUS
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-app-1 tracking-tight mb-3">
            Status do sistema
          </h1>
          <p className="text-sm text-app-3 mb-10">
            Atualiza automaticamente a cada 30 segundos.
            {lastFetch && ` Última verificação: ${lastFetch.toLocaleTimeString("pt-BR")}.`}
          </p>

          {/* Estado geral */}
          <div className={`rounded-2xl p-6 mb-6 ${
            error
              ? "bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/40"
              : allOk
              ? "bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/40"
              : "bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/40"
          }`}>
            <div className="flex items-center gap-3">
              <span className={`w-3 h-3 rounded-full ${
                error      ? "bg-rose-500"
                : allOk    ? "bg-emerald-500"
                            : "bg-amber-500"
              }`} />
              <p className="font-semibold text-app-1">
                {error
                  ? "Não conseguimos acessar o status. Pode ser problema de rede temporário."
                  : allOk
                  ? "Todos os sistemas operacionais"
                  : "Alguns serviços com problemas"}
              </p>
            </div>
          </div>

          {/* Lista de serviços */}
          {!error && data && (
            <div className="space-y-2">
              {services.map(s => (
                <div
                  key={s.key}
                  className="flex items-center justify-between p-4 rounded-xl border border-app bg-app-surface"
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                      s.skipped ? "bg-slate-500/60 ring-2 ring-slate-500/20" :
                      s.ok      ? "bg-emerald-500" : "bg-rose-500"
                    }`} />
                    <div>
                      <p className="text-sm font-semibold text-app-1">{s.name}</p>
                      {s.error && (
                        <p className="text-xs text-rose-600 dark:text-rose-400 mt-0.5">{s.error}</p>
                      )}
                      {s.skipped && (
                        <p className="text-xs text-app-3 mt-0.5">{t("status.notMonitored")}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-app-3 font-mono">
                    {s.skipped       ? "—" :
                     s.lat == null   ? "—" :
                     s.ok            ? `${s.lat}ms` :
                                       "indisponível"}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Footer */}
          <div className="mt-12 pt-6 border-t border-app-subtle text-xs text-app-3">
            <p>
              Está com problemas que não aparecem aqui?{" "}
              <a
                href="mailto:suporte@millersolutions.com.br"
                className="text-app-2 hover:text-app-1 underline-offset-2 hover:underline"
              >
                suporte@millersolutions.com.br
              </a>
            </p>
            {data && (
              <p className="mt-2 text-[10px] text-app-3">
                Versão: {data.version} · Ambiente: {data.env}
              </p>
            )}
          </div>
        </main>
      </div>
    </>
  );
}
